import {cdnImage} from '@/util/cdnImage';
import {MUIButton, MUIContainer} from '@/components/MUI-Components';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import React from 'react';

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        ethereumBannerBottom,
        ethereumBannerDataPoint,
        ethereumBlockchainService,
        ethereumChooseBody,
        ethereumChooseContent,
        ethereumDecodingContent,
        ethereumDevelopmentCheckPoint,
        ethereumDevelopmentTechnologies,
        ethereumDevFeature,
        ethereumFAQ,
        ethereumIndustryService,
        ethereumMobileProcess,
        ethereumSpecialStandard,
        ethereumTokenGeniusPoint
    } = await import('@/util/contents/ethereum-service-data');

    const {ndaxMPCData, ndaxMPCCheckPoints, ndaxButton} = await import('@/util/contents/web3-wallet-development-company');

    const {decentralizedNdaxPoint} = await import('@/util/contents/decentralized-development');

    return {
        props: {
            ethereumBannerBottom,
            ethereumBannerDataPoint,
            ethereumBlockchainService,
            ethereumChooseBody,
            ethereumChooseContent,
            ethereumDecodingContent,
            ethereumDevelopmentCheckPoint,
            ethereumDevelopmentTechnologies,
            ethereumDevFeature,
            ethereumFAQ,
            ethereumIndustryService,
            ethereumMobileProcess,
            ethereumSpecialStandard,
            ethereumTokenGeniusPoint,
            ndaxMPCData,
            ndaxMPCCheckPoints,
            ndaxButton,
            decentralizedNdaxPoint
        }
    };
};

const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
const Banner = dynamic(() => import('@/components/Ui-components/Banner'));
const Decoding = dynamic(() => import('@/components/Ui-components/Decoding'));
const auditImg = cdnImage('Ethereum-Development-Services/Ethereum-overview.webp');
import {ETHEREUM_DEVELOPMENT_SERVICE} from '@/util/metaData/blockchain';
const HelperSection = dynamic(() => import('@/components/Ui-components/helper-section'));
import DoneIcon from '@mui/icons-material/Done';
const statics = cdnImage('Ethereum-Development-Services/Ethereum-Blockchain-Growth.webp');
import parse from 'html-react-parser';
const Platforms = dynamic(() => import('@/components/Ui-components/platforms'));
const LatestCTA = dynamic(() => import('@/components/Ui-components/LatestCta'));
const DynamicScreens = dynamic(() => import('@/components/Ui-components/Dynamic-screens'));
const ndax = cdnImage('defi/NDAX.webp');
const genius = cdnImage('BEP-20/TokenGenius-Platform.webp');
const CardTechnology = dynamic(() => import('@/components/Ui-components/card-technology'));
const MobileProcess = dynamic(() => import('@/components/Ui-components/MobileProcess'));
const development = cdnImage('Ethereum-Development-Services/Ethereum-Blockchain-Development-Process.svg');
const OurMakeChoice = dynamic(() => import('@/components/Ui-components/OurMakeChoice'));
const LetsDiscuss = dynamic(() => import('@/components/Ui-components/Lets-discuss'));
const Questions = dynamic(() => import('@/components/Ui-components/questions'));
const ndaxExchange = cdnImage('Smart-contract-auditing/NDAX-Exchange.webp');
import AggregateScript from '@/components/Common/Aggregate-script';
import {SITE_URL} from '@/constants/urls';
import {colors} from '@/styles/tokens';

// Migrated from globals.scss `.cardano-blockchain-sprill` (owner re-attributed here per Unit B3)
const cardanoBlockchainSprillSx = {right: '300px !important'} as const;

const EthereumService = (props: any) => {
    const {
        openForm,
        ethereumBannerBottom,
        ethereumBannerDataPoint,
        ethereumBlockchainService,
        ethereumChooseBody,
        ethereumChooseContent,
        ethereumDecodingContent,
        ethereumDevelopmentCheckPoint,
        ethereumDevelopmentTechnologies,
        ethereumDevFeature,
        ethereumFAQ,
        ethereumIndustryService,
        ethereumMobileProcess,
        ethereumSpecialStandard,
        ethereumTokenGeniusPoint,
        ndaxMPCData,
        ndaxMPCCheckPoints,
        ndaxButton,
        decentralizedNdaxPoint
    } = props;
    return (
        <Box>
            <PageHead meta={ETHEREUM_DEVELOPMENT_SERVICE} url="/ethereum-development-services" />
            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="Ethereum Development Services"
                subheading="We develop scalable, secure, and enterprise-ready solutions using the Ethereum blockchain platform, helping businesses streamline operations, enhance security, and drive innovation. With a focus on smart contracts, tokenization, and DeFi integration, our services enable seamless blockchain adoption across industries like finance, supply chain, and digital identity."
                image={cdnImage('Ethereum-Development-Services/Ethereum-Development-Services.webp')}
                // bannerBackground="ethereum-banner-background"
                bannerClass="multicurrency-development-banner"
                alt="Ethereum Development Services"
                button="Schedule a Strategy Call"
                DataPoints={ethereumBannerDataPoint}
            />
            <Box className="sub-banner-wrapper sub-banner-bottom-wrapper">
                <MUIContainer>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between" className="white-label-bottom-container">
                        <Grid item md={2} lg={1.7} sm="auto" xs={12}>
                            <Typography className="banner-bottom-heading">Our Strategic Alliances</Typography>
                        </Grid>
                        {ethereumBannerBottom?.map((data: any, index: number) => (
                            <Grid item sm="auto" xs={6} className="sub-banner-images" key={index + 1}>
                                <Box className="images-banner-bottom">
                                    <DisplayImage src={data.src} alt={data.alt} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MUIContainer>
            </Box>

            <Grid item xs={12} className="bottom-margin top-space decording-main-wrapper mobile-top-padding mobile-bottom-margin">
                <MUIContainer>
                    <Decoding
                        imageSrc={auditImg}
                        alt="What is Ethereum? – A Quick Overview for Enterprises"
                        heading="What is Ethereum? – A Quick Overview for Enterprises"
                        subHeading={ethereumDecodingContent}
                        isSocialMediaBtn={true}
                    />
                </MUIContainer>
            </Grid>
            <hr className="hr-line zero-margin" />
            <Grid item xs={12} className="bottom-spacing top-space ethereum-blockchain-enterprise mobile-top-padding mobile-bottom-padding">
                <MUIContainer>
                    <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                            <Box className="spectrum-header-wrapper solutions trend-dominate-text-wrapper">
                                <Typography className="spectrum-heading color-black " component="h2" pb={2}>
                                    Why Enterprises Are Embracing Ethereum Blockchain for Innovation and Growth
                                </Typography>
                                <Typography className="spectrum-sub-heading color-black" component="p">
                                    As businesses seek robust blockchain solutions, Ethereum stands out for its security, scalability, and
                                    versatility. Its proven infrastructure and extensive developer community make it a preferred choice for
                                    enterprises aiming to streamline operations and foster innovation. Key statistics highlighting
                                    Ethereum’s enterprise adoption:
                                </Typography>
                            </Box>
                            <Box>
                                {ethereumDevelopmentCheckPoint?.map((item: any, index: number) => (
                                    <Box className="done-icon-wrapper" key={index + 1}>
                                        <DoneIcon sx={{color: colors.text.orangeAccessible}} />
                                        <Box>
                                            <Typography className="done-subheading color-black">
                                                {item.subheading ? parse(item.subheading) : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                                <Box className="spectrum-header-wrapper solutions trend-dominate-text-wrapper">
                                    <Typography className="spectrum-sub-heading color-black" component="p">
                                        Ethereum&apos;s continuous advancements and widespread adoption position it as a catalyst for
                                        enterprise transformation. Are you ready to explore how Ethereum can reinvent your business
                                        operations?
                                    </Typography>
                                </Box>
                                <Box py={1}>
                                    <MUIButton
                                        className="nft-button"
                                        variant="contained"
                                        label="Talk to Our Blockchain Consultants"
                                        onClick={openForm}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box className="ethereum-enterprise-growth">
                                <DisplayImage src={statics} alt="Blockchain for Innovation and Growth" />
                            </Box>
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Grid>
            <Grid item xs={12} className="helper-section helper_section_parent">
                <Box className=" bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper solutions">
                            <Typography className="spectrum-heading color-black our-service-main-heading text-align-left" component="h2">
                                Our All-Inclusive Ethereum Development Services
                            </Typography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line divider-line-left" />
                            </Box>
                            <Box className="sub-heading-box" pb={4}>
                                <Typography className="spectrum-sub-heading color-black spacing">
                                    Our Ethereum blockchain development services provide businesses with secure, scalable, and
                                    enterprise-grade blockchain solutions. From custom dApps to{' '}
                                    <a target="_blank" href="/private-blockchain-development">
                                        private blockchain
                                    </a>{' '}
                                    implementations, we offer tailored services to meet diverse business needs.
                                </Typography>
                            </Box>
                        </Box>
                        <Grid container spacing={2}>
                            <HelperSection data={ethereumBlockchainService} className="home-helper-card" />
                        </Grid>
                    </MUIContainer>
                </Box>
            </Grid>
            <Grid item xs={12} className="blockchain-revolution-service bottom-spacing  mobile-bottom-padding top-space mobile-top-padding">
                <MUIContainer>
                    <Platforms
                        data={ethereumDevFeature}
                        heading="Key Features Powering Ethereum Blockchain Development Solutions"
                        subheading="From scalable smart contracts to high-performance transactions, our Ethereum blockchain development solutions are designed to meet the evolving needs of enterprises. Explore how our cutting-edge features enhance security, efficiency, and automation, helping businesses leverage the full potential of the Ethereum network."
                        containerClass="blockchain-card-container"
                        className="platform-key-industry-service no-border"
                    />
                </MUIContainer>
            </Grid>
            <Box className="bottom-spacing  mobile-bottom-padding">
                <LatestCTA
                    heading="Did You Know? Ethereum Powers Over 4,000 Active dApps and a $329B+ Market Cap!"
                    subheading="Leverage Ethereum’s scalability, security, and interoperability to gain a competitive edge. Let’s help you integrate Ethereum blockchain into your business."
                    buttonText="Get a Free Consultation"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="helper-section standard-world-card-wrapper">
                <Box className="helper_section_parent bottom-spacing top-space mobile-top-padding">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper solutions" pb={2}>
                            <Typography className="spectrum-heading color-black our-service-main-heading" component="h2">
                                Ethereum Blockchain Standards We Specialize In
                            </Typography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line" />
                            </Box>
                            <Typography className="spectrum-sub-heading color-black spacing">
                                Explore the Ethereum token standards we specialize in to build secure, scalable, and compliant blockchain
                                solutions. Whether you need fungible tokens, NFTs, or security tokens, our expertise ensures efficient smart
                                contract development tailored to your business needs.
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <HelperSection data={ethereumSpecialStandard} className="home-helper-card" />
                        </Grid>
                    </MUIContainer>
                </Box>
            </Grid>
            <Grid item xs={12} className="blockchain-revolution-service bottom-spacing mobile-bottom-padding top-space mobile-top-padding">
                <MUIContainer>
                    <Platforms
                        data={ethereumIndustryService}
                        heading="Driving Innovation Across Industries with Ethereum Blockchain Development"
                        subheading="As a trusted Ethereum blockchain development company, we offer a comprehensive suite of Ethereum blockchain development services, enabling businesses across industries to streamline operations, enhance security, and drive innovation with decentralized solutions. "
                        containerClass="blockchain-card-container"
                        className="platform-key-industry-service no-border"
                    />
                </MUIContainer>
            </Grid>
            <Box className="bottom-spacing  mobile-bottom-padding">
                <LatestCTA
                    heading="Need Help Defining Your Ethereum Blockchain Requirements? Let’s Talk!"
                    subheading="Every business has unique blockchain needs, and choosing the right Ethereum standards, smart contracts, or decentralized solutions can be complex. Our experts will assess your requirements and guide you toward building a secure, & scalable blockchain strategy."
                    buttonText="Book a Free Consultation"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="dynmaic-white-label-wrapper bottom-margin dynamic-bep20-wrapper mobile-bottom-margin">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black text-align-left" component="h2">
                            Showcasing Our Ethereum Blockchain Success Stories
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </Box>
                    </Box>
                    <DynamicScreens
                        imageSrc={ndax}
                        subHeading="NDAX Wallet"
                        heading="A Trailblazing Blockchain Wallet & Portfolio Management Solution"
                        subPoints={ndaxMPCData}
                        reverseOrder={true}
                        alt="NDAX Wallet"
                        checkItems={ndaxMPCCheckPoints}
                        openForm={openForm}
                        buttonData={ndaxButton}
                        imageClass="techSpeak-dynamic-image"
                        buttonClass="nft-button"
                        buttonLabel="Request a Quote"
                    />
                    <DynamicScreens
                        imageSrc={ndaxExchange}
                        subHeading="NDAX Exchange"
                        heading="A Comprehensive Decentralized Crypto Exchange for Modern Trading Needs"
                        subPoints={[
                            'Built by Debut Infotech, empowers users with a decentralized exchange platform combining advanced features like smart contracts, automated market makers, and cross-chain compatibility. Our innovative approach ensures a user-friendly interface, optimized trading fees, and secure asset management, meeting the highest industry standards.'
                        ]}
                        reverseOrder={false}
                        alt="NDAX Exchange"
                        checkItems={decentralizedNdaxPoint}
                        openForm={openForm}
                        imageClass="techSpeak-dynamic-image"
                        buttonLabel="Request a Quote"
                        buttonClass="helper-card-button"
                    />
                    <DynamicScreens
                        imageSrc={genius}
                        subHeading="TokenGenius"
                        heading="Built a Token Development and ICO Launch Platform"
                        subPoints={[
                            "A leading blockchain startup, sought a secure and efficient solution for token development and ICO campaigns. Debut Infotech leveraged Ethereum blockchain technology, Solidity, and Web3.js to build a robust platform tailored to TokenGenius's needs. Our solution included secure token creation, ICO campaign management, and regulatory compliance features to attract investors and ensure trust."
                        ]}
                        reverseOrder={true}
                        alt="TokenGenius"
                        checkItems={ethereumTokenGeniusPoint}
                        openForm={openForm}
                        buttonClass="nft-button"
                        buttonLabel="Request a Quote"
                    />
                </MUIContainer>
            </Grid>
            <Grid
                item
                xs={12}
                className="bottom-spacing generative-tech-wrapper mobile-bottom-padding top-space mobile-top-padding helper_section_parent"
            >
                <CardTechnology
                    heading="Technologies We Employ to Build Ethereum-Powered Solutions"
                    subheading="Our Ethereum blockchain development team builds secure, scalable, and high-performance solutions tailored to business needs. By leveraging cutting-edge technologies, we enhance transaction efficiency, security, and interoperability, ensuring businesses can seamlessly integrate blockchain, automate processes, and drive innovation in an increasingly decentralized digital landscape."
                    data={ethereumDevelopmentTechnologies}
                />
            </Grid>
            <Box className="bottom-spacing process-mobile-bottom-padding top-space mobile-top-padding">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black text-align-left" component="h2">
                            Our Structured Ethereum Blockchain Development Process
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </Box>
                        <Box className="sub-heading-box" pb={4}>
                            <Typography className="spectrum-sub-heading color-black spacing text-align-left" component="p">
                                With a focus on business objectives and technical excellence, we develop Ethereum-based blockchain solutions
                                that ensure scalability, security, and seamless integration. Every stage of our process is tailored to meet
                                your unique industry needs.
                            </Typography>
                        </Box>
                    </Box>
                    <Box textAlign="center">
                        <DisplayImage
                            src={development}
                            alt="Ethereum Blockchain Development Process"
                            className="approach-main-img approach-web-img"
                        />
                    </Box>
                    <Box className="approach-main-img approach-mobile-img">
                        <MobileProcess mobileProcess={ethereumMobileProcess} />
                    </Box>
                </MUIContainer>
            </Box>
            <OurMakeChoice
                heading="Why Businesses"
                subheading="Trust Debut Infotech for Ethereum Development Services"
                body={ethereumChooseBody}
                contentData={ethereumChooseContent}
            />
            <Grid item xs={12} className="bottom-spacing">
                <LetsDiscuss
                    heading="Still Unsure How Ethereum Can Transform Your Business?"
                    subheading="We can help you cut through the noise with expert Ethereum consultation. From understanding your requirements to strategic implementation, our expertise ensures informed decisions that align with your business goals."
                    btnLabel="Schedule a Quick Call"
                    imageSx={cardanoBlockchainSprillSx}
                    openForm={() => {
                        globalThis.open('/contact-us', '_blank');
                    }}
                />
            </Grid>
            <Grid item xs={12} className="question-parent top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={ethereumFAQ} subpart={true} />
                </MUIContainer>
            </Grid>
            <script
                type="application/ld+json"
                id="ethereum-development-service"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'Is Ethereum a Blockchain or a Cryptocurrency?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Ethereum is both a blockchain platform and a cryptocurrency. The Ethereum blockchain is a decentralized, open-source network that enables smart contract execution and dApp development, while Ether (ETH) is its native cryptocurrency used to pay for transactions and computational power on the network.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is the Ethereum Blockchain?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Ethereum is a decentralized blockchain platform that allows developers to create and deploy smart contracts and decentralized applications (dApps). It runs on a global network of nodes, ensuring security, transparency, and resistance to censorship.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How to Create a Blockchain Like Ethereum?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Creating a blockchain similar to Ethereum requires:
Designing a decentralized network with a consensus mechanism (Proof of Stake or Proof of Work)
Developing a virtual machine for executing smart contracts
Implementing token standards and governance mechanisms
Ensuring network security and scalability`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Is Solana on the Ethereum Blockchain?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'No, Solana is an independent blockchain with its own consensus mechanism (Proof of History + Proof of Stake). While both Ethereum and Solana support smart contracts and decentralized applications, they operate separately. However, cross-chain bridges enable token and asset transfers between them.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is an ERC-20 Token?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'An ERC-20 token is a fungible token standard used on the Ethereum blockchain. It defines rules that all Ethereum-based tokens must follow, ensuring compatibility across exchanges and wallets. It is widely used in DeFi, gaming, and tokenized assets.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is Gas in Ethereum?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Ethereum gas fees represent the computational cost of executing transactions and smart contracts. Measured in gwei, gas ensures that network participants (validators) are incentivized to process transactions securely.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How Does Ethereum Differ from Bitcoin?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Bitcoin is primarily a digital currency designed for peer-to-peer transactions.
Ethereum is a programmable blockchain that enables smart contracts and dApps, supporting DeFi, NFTs, and enterprise solutions.`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is the Ethereum Virtual Machine (EVM)?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'The Ethereum Virtual Machine (EVM) is a global computing environment that executes smart contracts on Ethereum nodes, ensuring consistency, security, and interoperability across dApps.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is Proof of Stake (PoS) in Ethereum?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Ethereum transitioned to Proof of Stake (PoS) with the Ethereum 2.0 upgrade, reducing energy consumption and enhancing network scalability. PoS selects validators based on the amount of ETH staked, making the network more sustainable and efficient.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How Much Does It Cost to Develop an Ethereum Blockchain Solution?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `The cost of developing an Ethereum blockchain solution depends on multiple factors, including:
Complexity of the Project – Basic smart contracts cost less than full-fledged DeFi platforms or enterprise dApps.
Development Time & Resources – The more advanced the solution, the higher the development cost.
Customization & Integrations – Integrating Layer-2 scaling, APIs, cross-chain compatibility, or custom token standards increases development expenses.
Security & Compliance Requirements – Smart contract auditing, regulatory compliance, and security enhancements can add to the cost.
Estimated Cost Range:
✔ Basic Smart Contract Development: $5,000 – $20,000
✔ dApp Development: $30,000 – $100,000+
✔ Enterprise Blockchain Solutions: $100,000 – $500,000+
For an accurate quote, it’s best to consult our blockchain experts to assess project requirements.`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How Long Does It Take to Develop an Ethereum-Based Project?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `The timeline depends on the project’s scope, features, and level of customization. Here’s a general breakdown:
✔ Basic Smart Contract: 2 – 4 weeks
✔ Custom dApp Development: 3 – 6 months
✔ Enterprise Ethereum Solutions: 6 – 12+ months
The timeline includes:
Requirement Gathering & Planning (1 – 2 weeks)
Smart Contract & dApp Development (varies based on complexity)
Testing & Security Audits (2 – 4 weeks)
Deployment & Ongoing Support (continuous post-launch optimization)`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can You Develop a Custom Ethereum Blockchain for My Business?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, we offer custom Ethereum blockchain solutions tailored to your business needs. Whether you need DeFi applications, NFT marketplaces, tokenization solutions, or enterprise-grade blockchain networks, our team ensures a secure, scalable, and future-ready implementation.'
                                }
                            }
                        ]
                    })
                }}
            />
            <AggregateScript
                name="Ethereum Development"
                image="https://debut-dev.debutinfotech.in/_next/image?url=%2Fimages%2FEthereum-Development-Services%2FEthereum-Development-Services.webp&w=750&q=75"
                description="Unlock the power of decentralized applications with Ethereum development. Build secure, scalable, and smart contract-powered solutions for finance, gaming, and enterprise use cases. Leverage Ethereum’s blockchain to create innovative dApps, DeFi platforms, and NFTs with expert development services."
                offers={{
                    '@type': 'AggregateOffer',
                    url: `${SITE_URL}/ethereum-development-services`,
                    priceCurrency: 'USD',
                    lowPrice: '10000',
                    highPrice: '300000',
                    offerCount: '10'
                }}
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.8',
                    bestRating: '5',
                    worstRating: '2',
                    ratingCount: '399'
                }}
            />
        </Box>
    );
};

export default EthereumService;
EthereumService.Layout = 'Main';
